import React, { useEffect, useMemo, useRef } from 'react';

import L from 'leaflet';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

import classes from '@/components/SchedulePage/ScheduleMap/style.module.scss';
import flexibleTimeIcon from '@/components/SchedulePage/ScheduleMap/icons/flexible-time.svg';
import fixedTimeIcon from '@/components/SchedulePage/ScheduleMap/icons/fixed-time.svg';
import {
  DEFAULT_BASEMAP_ID,
  getBasemapProvider,
} from '@/components/SchedulePage/ScheduleMap/helpers/basemaps';

const DEFAULT_CENTER = [-23.55052, -46.633308];
const DEFAULT_ZOOM = 13;

const escapeHtml = value =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const createPropertyMarkerIcon = (marker, isSelected, badgeLabels) => {
  const flexibleTimeLabel = escapeHtml(badgeLabels.flexibleTime);
  const fixedTimeLabel = escapeHtml(badgeLabels.fixedTime);
  let badgeHtml = '';

  if (marker.isFlextime) {
    if (marker.inspectorInitials) {
      badgeHtml = `
        <div class="${classes.propertyBadge} ${classes.flexiblePillBadge} ${
        classes.scheduleTimeBadgeOffset
      }" style="color: ${marker.inspectorColor || '#00c3e6'}; border-color: ${
        marker.inspectorColor || '#00c3e6'
      };" title="${flexibleTimeLabel}">
          <img class="${classes.badgeIconImg}" src="${escapeHtml(flexibleTimeIcon)}" alt="${flexibleTimeLabel}" />
          <span class="${classes.badgeText}">${escapeHtml(marker.inspectorInitials)}</span>
        </div>
      `;
    } else {
      badgeHtml = `
        <div class="${classes.propertyBadge} ${classes.flexibleBadge} ${
        classes.scheduleTimeBadgeOffset
      }" title="${flexibleTimeLabel}">
          <img class="${classes.badgeIconImg}" src="${escapeHtml(flexibleTimeIcon)}" alt="${flexibleTimeLabel}" />
        </div>
      `;
    }
  } else if (marker.isAccompanied || marker.scheduleType === 'fixed') {
    if (marker.inspectorInitials) {
      badgeHtml = `
        <div class="${classes.propertyBadge} ${classes.fixedPillBadge} ${
        classes.scheduleTimeBadgeOffset
      }" style="color: ${marker.inspectorColor || '#00c3e6'}; border-color: ${
        marker.inspectorColor || '#00c3e6'
      };" title="${fixedTimeLabel}">
          <img class="${classes.badgeIconImg}" src="${escapeHtml(fixedTimeIcon)}" alt="${fixedTimeLabel}" />
          <span class="${classes.badgeText}">${escapeHtml(marker.inspectorInitials)}</span>
        </div>
      `;
    } else {
      badgeHtml = `
        <div class="${classes.propertyBadge} ${classes.fixedCircleBadge} ${
        classes.scheduleTimeBadgeOffset
      }" title="${fixedTimeLabel}">
          <img class="${classes.badgeIconImg}" src="${escapeHtml(fixedTimeIcon)}" alt="${fixedTimeLabel}" />
        </div>
      `;
    }
  } else if (marker.inspectorInitials) {
    badgeHtml = `
      <div class="${classes.propertyBadge}" style="background-color: ${
      marker.inspectorColor || '#00c3e6'
    }; border-color: #ffffff;">
        ${escapeHtml(marker.inspectorInitials)}
      </div>
    `;
  }

  return L.divIcon({
    className: `${classes.markerWrapper} ${isSelected ? classes.selectedMarker : ''}`,
    html: `
      <div class="${classes.propertyPin}" style="border-color: ${marker.inspectorColor || '#00c3e6'};">
        <img class="${classes.propertyIconImg}" src="${escapeHtml(marker.orderIcon)}" alt="" />
        ${badgeHtml}
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
    tooltipAnchor: [0, -18],
  });
};

const createKeysGroupMarkerIcon = marker => {
  let summary = '';
  if (marker.pickupCount > 0) summary += `${marker.pickupCount} ⬆️ `;
  if (marker.returnCount > 0) summary += `${marker.returnCount} ⬇️`;

  return L.divIcon({
    className: classes.markerWrapper,
    html: `
      <div class="${classes.keysGroupPill}">
        <span class="${classes.keyEmoji}">🔑</span>
        <span class="${classes.keySummaryText}">${escapeHtml(summary.trim())}</span>
      </div>
    `,
    iconSize: [95, 28],
    iconAnchor: [47, 14],
    popupAnchor: [0, -14],
    tooltipAnchor: [0, -14],
  });
};

const createInspectorBaseMarkerIcon = marker =>
  L.divIcon({
    className: classes.markerWrapper,
    html: `
      <div class="${classes.inspectorBasePin}" style="border-color: ${marker.inspectorColor}; color: ${
      marker.inspectorColor
    };">
        <svg class="${
          classes.inspectorBaseIconSvg
        }" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M4 10.9C4 10.58 4.14 10.28 4.39 10.08L11.14 4.68C11.64 4.28 12.36 4.28 12.86 4.68L19.61 10.08C19.86 10.28 20 10.58 20 10.9V18.5C20 19.33 19.33 20 18.5 20H14.6C13.94 20 13.4 19.46 13.4 18.8V15.7C13.4 15.04 12.86 14.5 12.2 14.5H11.8C11.14 14.5 10.6 15.04 10.6 15.7V18.8C10.6 19.46 10.06 20 9.4 20H5.5C4.67 20 4 19.33 4 18.5V10.9Z" fill="${escapeHtml(
            marker.inspectorColor || '#2ea66a'
          )}"/>
        </svg>
        <span class="${classes.inspectorBaseBadge}" style="border-color: ${marker.inspectorColor}; color: ${
      marker.inspectorColor
    };">
          ${escapeHtml(marker.inspectorInitials)}
        </span>
      </div>
    `,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -24],
    tooltipAnchor: [0, -24],
  });

const createOrdersGroupMarkerIcon = (marker, isSelected) =>
  L.divIcon({
    className: `${classes.markerWrapper} ${isSelected ? classes.selectedMarker : ''}`,
    html: `
      <div class="${classes.propertyPin} ${classes.ordersGroupPin}">
        <span class="${classes.groupIconEmoji}">🏢</span>
        <div class="${classes.groupBadge}">${marker.count}</div>
      </div>
    `,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -19],
    tooltipAnchor: [0, -19],
  });

const createMarkerIcon = (marker, isSelected, badgeLabels) => {
  if (['scheduledOrder', 'waitingOrder'].includes(marker.type)) {
    return createPropertyMarkerIcon(marker, isSelected, badgeLabels);
  }

  if (marker.type === 'ordersGroup') {
    return createOrdersGroupMarkerIcon(marker, isSelected);
  }

  if (marker.type === 'keysGroup') {
    return createKeysGroupMarkerIcon(marker);
  }

  if (marker.type === 'inspectorBase') {
    return createInspectorBaseMarkerIcon(marker);
  }

  return L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });
};

const iconCacheMap = new Map();

const getMemoizedMarkerIcon = (marker, badgeLabels, locale, variant = 'default') => {
  const isSelected = variant === 'selected';
  const cacheKey = [
    locale,
    marker.id,
    variant,
    marker.type,
    marker.orderIcon || 'none',
    marker.isFlextime ? 'fx1' : 'fx0',
    marker.isAccompanied ? 'ac1' : 'ac0',
    marker.scheduleType || 'none',
    marker.inspectorInitials || 'none',
    marker.inspectorColor || 'none',
  ].join('_');

  if (iconCacheMap.has(cacheKey)) {
    return iconCacheMap.get(cacheKey);
  }

  const icon = createMarkerIcon(marker, isSelected, badgeLabels);

  if (iconCacheMap.size > 1000) {
    iconCacheMap.clear();
  }
  iconCacheMap.set(cacheKey, icon);

  return icon;
};

const FitVisibleMarkers = ({ markers, scheduleDate }) => {
  const map = useMap();
  const hasFittedForDateRef = useRef(null);

  useEffect(() => {
    hasFittedForDateRef.current = null;
  }, [scheduleDate]);

  useEffect(() => {
    if (!markers.length) return;
    if (hasFittedForDateRef.current === scheduleDate) return;

    const propertyMarkers = markers.filter(m => ['scheduledOrder', 'waitingOrder', 'ordersGroup'].includes(m.type));
    const rawTargetMarkers = propertyMarkers.length > 0 ? propertyMarkers : markers;
    const targetMarkers = rawTargetMarkers.filter(m => {
      const [lat, lng] = m?.position || [];
      const latNum = Number(lat);
      const lngNum = Number(lng);
      return Number.isFinite(latNum) && Number.isFinite(lngNum) && (latNum !== 0 || lngNum !== 0);
    });

    if (!targetMarkers.length) return;

    const bounds = L.latLngBounds(targetMarkers.map(marker => marker.position));

    if (bounds.isValid()) {
      if (targetMarkers.length === 1) {
        map.setView(targetMarkers[0].position, 14, { animate: true });
      } else {
        map.fitBounds(bounds, { animate: true, maxZoom: 14, padding: [50, 50] });
        if (map.getZoom() < 11) {
          map.setZoom(11);
        }
      }
      hasFittedForDateRef.current = scheduleDate;
    }
  }, [map, markers, scheduleDate]);

  return null;
};

FitVisibleMarkers.propTypes = {
  markers: PropTypes.arrayOf(PropTypes.object).isRequired,
  scheduleDate: PropTypes.string,
};

FitVisibleMarkers.defaultProps = {
  scheduleDate: null,
};

const InvalidateMapSize = ({ isVisible, activeBasemapId }) => {
  const map = useMap();

  useEffect(() => {
    if (!isVisible) return undefined;

    const container = map.getContainer();
    if (!container) return undefined;

    let rafId = null;

    const triggerInvalidate = () => {
      if (rafId) cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        try {
          map.invalidateSize({ animate: false });
        } catch {
          // silent fallback
        }
      });
    };

    triggerInvalidate();
    const t1 = setTimeout(triggerInvalidate, 50);
    const t2 = setTimeout(triggerInvalidate, 200);
    const t3 = setTimeout(triggerInvalidate, 500);

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        triggerInvalidate();
      });
      observer.observe(container);
    }

    window.addEventListener('resize', triggerInvalidate);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (rafId) cancelAnimationFrame(rafId);
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', triggerInvalidate);
    };
  }, [activeBasemapId, isVisible, map]);

  return null;
};

InvalidateMapSize.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  activeBasemapId: PropTypes.string,
};

InvalidateMapSize.defaultProps = {
  activeBasemapId: null,
};

const FocusMapPosition = ({ focusRequest }) => {
  const map = useMap();

  useEffect(() => {
    if (!focusRequest?.position || !Array.isArray(focusRequest.position) || focusRequest.position.length < 2) {
      return;
    }

    const [lat, lng] = focusRequest.position;
    const latNum = Number(lat);
    const lngNum = Number(lng);

    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
      return;
    }

    if (latNum === 0 && lngNum === 0) {
      return;
    }

    if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
      return;
    }

    const currentZoom = map.getZoom();
    const targetZoom = Number.isFinite(currentZoom) ? Math.max(currentZoom, 15) : 15;

    try {
      map.flyTo([latNum, lngNum], targetZoom, {
        animate: true,
        duration: 0.8,
      });
    } catch {
      try {
        map.setView([latNum, lngNum], targetZoom);
      } catch {
        // ignore invalid Leaflet map state errors
      }
    }
  }, [focusRequest, map]);

  return null;
};

FocusMapPosition.propTypes = {
  focusRequest: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    position: PropTypes.arrayOf(PropTypes.number).isRequired,
  }),
};

FocusMapPosition.defaultProps = {
  focusRequest: null,
};

const ScheduleLeafletMap = ({
  activeBasemapId,
  markers,
  polylines,
  focusRequest,
  selectedOrderId,
  isVisible,
  scheduleDate,
  onOrderMarkerClick,
}) => {
  const intl = useIntl();
  const badgeLabels = useMemo(
    () => ({
      flexibleTime: intl.formatMessage({ id: 'FLEXTIME_BADGE_TOOLTIP' }),
      fixedTime: intl.formatMessage({ id: 'SCHEDULE_MAP.FILTER.SCHEDULE_FIXED' }),
    }),
    [intl]
  );

  const markersWithIcons = useMemo(
    () =>
      markers.map(marker => ({
        ...marker,
        baseIcon: getMemoizedMarkerIcon(marker, badgeLabels, intl.locale),
      })),
    [badgeLabels, intl.locale, markers]
  );

  const selectedMarkerId = selectedOrderId ? String(selectedOrderId) : null;

  const selectedMarker = useMemo(
    () => markersWithIcons.find(marker => String(marker.id) === selectedMarkerId) || null,
    [markersWithIcons, selectedMarkerId]
  );

  const selectedMarkerIcon = useMemo(
    () => (selectedMarker ? getMemoizedMarkerIcon(selectedMarker, badgeLabels, intl.locale, 'selected') : null),
    [badgeLabels, intl.locale, selectedMarker]
  );

  const activeBasemap = useMemo(
    () => getBasemapProvider(activeBasemapId),
    [activeBasemapId]
  );

  return (
    <MapContainer
      key={scheduleDate || 'schedule-map'}
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      className={classes.leafletMap}
    >
      <TileLayer
        key={activeBasemap.id}
        attribution={activeBasemap.attribution}
        url={activeBasemap.url}
        subdomains={activeBasemap.subdomains || 'abc'}
        maxZoom={activeBasemap.maxZoom || 19}
      />

      <FitVisibleMarkers markers={markers} scheduleDate={scheduleDate} />
      <InvalidateMapSize isVisible={isVisible} activeBasemapId={activeBasemapId} />
      <FocusMapPosition focusRequest={focusRequest} />

      {polylines.map(line => (
        <Polyline
          key={line.id}
          positions={line.positions}
          pathOptions={{
            color: line.color,
            dashArray: '6, 8',
            weight: 2.5,
            opacity: 0.7,
          }}
        />
      ))}

      {markersWithIcons.map(marker => (
        <Marker
          key={`${marker.type}:${marker.id}`}
          position={marker.position}
          icon={selectedMarkerIcon && selectedMarkerId === String(marker.id) ? selectedMarkerIcon : marker.baseIcon}
          eventHandlers={
            ['scheduledOrder', 'waitingOrder'].includes(marker.type)
              ? { click: () => onOrderMarkerClick(String(marker.id)) }
              : undefined
          }
        >
          {marker.type === 'ordersGroup' ? (
            <Popup>
              <div className={classes.popupContent}>
                <div className={classes.popupHeader}>
                  <span className={classes.popupHeaderIcon}>🏢</span>
                  <strong>
                    {marker.address || intl.formatMessage({ id: 'SCHEDULE_MAP.GROUPS.TITLE' }, { count: marker.count })}
                  </strong>
                </div>

                <div className={classes.popupSubheader}>
                  {intl.formatMessage({ id: 'SCHEDULE_MAP.GROUPS.ITEM_COUNT' }, { count: marker.count })}
                </div>

                <div className={classes.popupList}>
                  {marker.items?.map((item, idx) => (
                    <div
                      key={item.id || item.public_id || `${item.title}-${idx}`}
                      className={`${classes.popupListItem} ${item.rawOrder ? classes.clickableListItem : ''}`}
                      onClick={() => {
                        if (item.rawOrder) {
                          onOrderMarkerClick(String(item.public_id || item.id));
                        }
                      }}
                    >
                      <div className={classes.popupItemTitleRow}>
                        <span style={{ color: item.inspectorColor || '#007bff', fontWeight: 'bold' }}>
                          ● {item.title}
                        </span>
                      </div>
                      {item.inspectorName && (
                        <div className={classes.popupItemDetail}>
                          👤 {intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.INSPECTOR' })}: {item.inspectorName}
                        </div>
                      )}
                      {item.scheduleInfo && (
                        <div className={classes.popupItemDetail}>
                          🕒 {intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.SCHEDULE' })}: {item.scheduleInfo}
                        </div>
                      )}
                      {item.preScheduleInfo && (
                        <div className={classes.popupItemDetail}>
                          📅 {intl.formatMessage({ id: 'SCHEDULE_DATE' })}: {item.preScheduleInfo}
                        </div>
                      )}
                      {item.accompaniedInfo && (
                        <div className={classes.popupItemDetail}>
                          🤝 {intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.ACCOMPANIED' })}: {item.accompaniedInfo}
                        </div>
                      )}
                      {item.liberatedInfo && (
                        <div className={classes.popupItemDetail}>
                          🔑 {intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.LIBERATED' })}: {item.liberatedInfo}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Popup>
          ) : marker.type === 'keysGroup' ? (
            <Popup>
              <div className={classes.popupContent}>
                <div className={classes.popupHeader}>
                  <span className={classes.popupHeaderIcon}>🔑</span>
                  <strong>{marker.locationName}</strong>
                </div>

                <div className={classes.popupSubheader}>
                  {intl.formatMessage(
                    { id: 'SCHEDULE_MAP.KEYS.SUMMARY' },
                    { pickupCount: marker.pickupCount, returnCount: marker.returnCount }
                  )}
                </div>

                <div className={classes.popupList}>
                  {marker.items?.map((item, idx) => (
                    <div
                      key={item.id || item.public_id || `${item.inspectorName}-${item.action}-${idx}`}
                      className={classes.popupListItem}
                    >
                      <span style={{ color: item.inspectorColor, fontWeight: 'bold' }}>● {item.inspectorName}</span>
                      <span className={classes.popupActionText}>
                        {item.action === 'pickup'
                          ? intl.formatMessage({ id: 'SCHEDULE_MAP.KEYS.PICKUP' })
                          : intl.formatMessage({ id: 'SCHEDULE_MAP.KEYS.RETURN' })}{' '}
                        ({item.orderLabel})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Popup>
          ) : (
            <Tooltip direction="top" className={classes.leafletTooltipCustom}>
              <div className={classes.tooltipContainer}>
                <div className={classes.tooltipTitle}>{marker.title}</div>

                {marker.inspectorName && (
                  <div className={classes.tooltipLine}>
                    👤 <strong>{intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.INSPECTOR' })}:</strong>{' '}
                    {marker.inspectorName}
                  </div>
                )}
                {marker.address && <div className={classes.tooltipLine}>📍 {marker.address}</div>}
                {marker.scheduleInfo && (
                  <div className={classes.tooltipLine}>
                    🕒 <strong>{intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.SCHEDULE' })}:</strong>{' '}
                    {marker.scheduleInfo}
                  </div>
                )}
                {marker.preScheduleInfo && (
                  <div className={classes.tooltipLine}>
                    📅 <strong>{intl.formatMessage({ id: 'SCHEDULE_DATE' })}:</strong> {marker.preScheduleInfo}
                  </div>
                )}
                {marker.accompaniedInfo && (
                  <div className={classes.tooltipLine}>
                    🤝 <strong>{intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.ACCOMPANIED' })}:</strong>{' '}
                    {marker.accompaniedInfo}
                  </div>
                )}
                {marker.liberatedInfo && (
                  <div className={classes.tooltipLine}>
                    🔑 <strong>{intl.formatMessage({ id: 'SCHEDULE_MAP.TOOLTIP.LIBERATED' })}:</strong>{' '}
                    {marker.liberatedInfo}
                  </div>
                )}
                {marker.detail && !marker.inspectorName && <div className={classes.tooltipDetail}>{marker.detail}</div>}
              </div>
            </Tooltip>
          )}
        </Marker>
      ))}
    </MapContainer>
  );
};

ScheduleLeafletMap.propTypes = {
  activeBasemapId: PropTypes.string,
  markers: PropTypes.arrayOf(PropTypes.object).isRequired,
  polylines: PropTypes.arrayOf(PropTypes.object).isRequired,
  focusRequest: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    position: PropTypes.arrayOf(PropTypes.number).isRequired,
  }),
  selectedOrderId: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  scheduleDate: PropTypes.string,
  onOrderMarkerClick: PropTypes.func.isRequired,
};

ScheduleLeafletMap.defaultProps = {
  activeBasemapId: DEFAULT_BASEMAP_ID,
  focusRequest: null,
  selectedOrderId: null,
  scheduleDate: null,
};

export default React.memo(ScheduleLeafletMap);
